import React, { useState, useMemo, useEffect } from "react";
import { FaSearch, FaFilter } from "react-icons/fa";
import DropdownSelect from "./DropdownSelect";

const ZONES = [
  "All States",
  "North Central",
  "North East",
  "North West",
  "South East",
  "South South",
  "South West",
];
const SORT_OPTIONS = ["Alphabetical", "Achievement", "Zone"];

function getStatus(achievement) {
  if (achievement >= 70) return "HIGH PERFORMANCE";
  if (achievement >= 40) return "ON TRACK";
  return "ATTENTION REQUIRED";
}

const STATUS_CONFIG = {
  "HIGH PERFORMANCE": { color: "#006B3F", badgeBg: "#e8f5e9" },
  "ON TRACK": { color: "#d97706", badgeBg: "#fef3c7" },
  "ATTENTION REQUIRED": { color: "#dc2626", badgeBg: "#fee2e2" },
};


function StateCardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 animate-pulse">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="rounded-lg bg-gray-200 flex-shrink-0" style={{ width: "48px", height: "48px" }} />
          <div>
            <div className="h-5 bg-gray-200 rounded w-24 mb-2" />
            <div className="h-3 bg-gray-200 rounded w-20" />
          </div>
        </div>
        <div className="text-right">
          <div className="h-3 bg-gray-200 rounded w-16 mb-2" />
          <div className="h-7 bg-gray-200 rounded w-12" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i}>
            <div className="h-2 bg-gray-200 rounded w-20 mb-1" />
            <div className="h-3 bg-gray-200 rounded w-16" />
          </div>
        ))}
      </div>
      <div className="flex justify-between mb-2">
        <div className="h-2 bg-gray-200 rounded w-24" />
        <div className="h-2 bg-gray-200 rounded w-20" />
      </div>
      <div className="h-2 bg-gray-200 rounded w-full" />
    </div>
  );
}

function StateEmblem({ stateId, name }) {
  return (
    <div
      className="flex items-center justify-center flex-shrink-0 border border-gray-200 overflow-hidden"
      style={{ width: "50px", height: "50px", background: "#F3F3F6", borderRadius: "8px" }}
    >
      <img
        src={`/assets/img/${stateId}.png`}
        alt={name}
        style={{ width: "80%", height: "80%", objectFit: "cover" }}
        onError={(e) => {
          e.currentTarget.style.display = "none";
          e.currentTarget.nextSibling.style.display = "flex";
        }}
      />
      <span
        className="text-xs font-bold w-full h-full items-center justify-center"
        style={{ color: "#006B3F", display: "none" }}
      >
        {name.replace(/[()]/g, "").trim().split(" ").map((w) => w[0]).slice(0, 2).join("")}
      </span>
    </div>
  );
}

function StateCard({ state }) {
  const achievement = state.achievement ?? 0;
  const status = getStatus(achievement);
  const { color, badgeBg } = STATUS_CONFIG[status];

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <StateEmblem stateId={state.id} name={state.name} />
          <div className="flex flex-col gap-0">
            <p className="font-bold text-gray-900 mb-0 mt-0" style={{ fontSize: "20px" }}>
              {state.name}
            </p>
            <span
              className="font-bold tracking-wider"
              style={{ color, background: badgeBg, padding: "1px 7px", borderRadius: "4px", fontSize: "10px" }}
            >
              {status}
            </span>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs mb-1 mt-0" style={{ color: "#3E4A41" }}>Achievement</p>
          <p className="font-bold mb-0 mt-0" style={{ fontSize: "24px", color, lineHeight: 1 }}>
            {achievement}%
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <div>
          <p className="mb-1 mt-0" style={{ color: "#3E4A41", fontSize: "11px" }}>LAST 30 DAYS</p>
          <p className="font-bold mb-0 mt-0" style={{ fontSize: "12px", color: "#1A1C1E" }}>
            {state.last30Days}
          </p>
        </div>
        <div>
          <p className="mb-1 mt-0" style={{ color: "#3E4A41", fontSize: "11px" }}>REGISTRARS</p>
          <p className="font-bold mb-0 mt-0" style={{ fontSize: "12px", color: "#1A1C1E" }}>
            {state.registrars.toLocaleString()} Active
          </p>
        </div>
      </div>

      <div className="mb-3">
        <p className="mb-1 mt-0" style={{ color: "#3E4A41", fontSize: "11px" }}>FACILITY/COMM</p>
        <p className="font-bold mb-0 mt-0" style={{ fontSize: "12px", color: "#1A1C1E" }}>
          {state.facilitySplit}
        </p>
      </div>
      <div className="flex justify-between mb-2">
        <p className="text-xs text-gray-500 mb-0 mt-0">Registered: {state.registered.toLocaleString()}</p>
        <p className="text-xs text-gray-500 mb-0 mt-0">Target: {state.target.toLocaleString()}</p>
      </div>
      <div className="h-2 rounded-full bg-gray-100">
        <div
          className="h-2 rounded-full"
          style={{ width: `${Math.min(achievement, 100)}%`, background: color, transition: "width 0.4s ease" }}
        />
      </div>
    </div>
  );
}

export default function States({ states = [], loading, initialZone = "All States" }) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("Alphabetical");
  const [zoneFilter, setZoneFilter] = useState(initialZone);

  useEffect(() => {
    setZoneFilter(initialZone);
  }, [initialZone]);

  const filtered = useMemo(() => {
    let list = states.filter((s) => {
      const matchSearch = s.name.toLowerCase().includes(search.toLowerCase());
      const matchZone = zoneFilter === "All States" || s.zone === zoneFilter;
      return matchSearch && matchZone;
    });
    if (sort === "Alphabetical") list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    if (sort === "Achievement") list = [...list].sort((a, b) => (b.achievement ?? 0) - (a.achievement ?? 0));
    if (sort === "Zone") list = [...list].sort((a, b) => a.zone.localeCompare(b.zone));
    return list;
  }, [states, search, sort, zoneFilter]);

  return (
    <div>
      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 mb-5">
        <div
          className="flex items-center gap-2 flex-1 bg-white border border-gray-200 rounded-lg px-4"
          style={{ height: "44px" }}
        >
          <FaSearch style={{ color: "#9ca3af", fontSize: "14px", flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Search State..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 text-sm text-gray-700 bg-transparent"
            style={{ border: "none", outline: "none", height: "100%" }}
          />
        </div>

        <DropdownSelect label="Sort By" value={sort} onChange={setSort} options={SORT_OPTIONS} />
        <DropdownSelect label="Zone" value={zoneFilter} onChange={setZoneFilter} options={ZONES} icon={FaFilter} />
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <StateCardSkeleton key={i} />)}
        </div>
      ) : filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((state) => (
            <StateCard key={state.id} state={state} />
          ))}
        </div>
      ) : (
        <p className="text-gray-400 text-sm text-center py-12 mb-0">No states match your search.</p>
      )}
    </div>
  );
}
